import React, { useState } from 'react'

import {
  Button,
  DropdownMenu,
  Icon,
  type IconStringList,
  type MenuProps,
  type PopoverProps,
  type TooltipProps,
  useIsMobileView,
} from '../../../module'
import styles from './_button-group.module.scss'
import { type ButtonProps } from '../Button.models'
import { c } from '../../../translations/LibraryTranslationService'

type ActionProps = {
  actions: MenuProps['actions']
  disabled?: boolean
  onClick?: never
  /** Optional tooltip configuration for the action button */
  tooltip?: Omit<TooltipProps, 'children'>
  confirmation?: never
  children?: never
  placement?: PopoverProps['position']
  /** Optionally override the default "options" icon for the action button */
  actionIcon?: IconStringList
  /** Optional prop to add a test id to the ButtonGroup for QA testing */
  qaTestId?: string
}

type StandardButtonProps = ButtonProps & {
  actions?: never
  placement?: never
  actionIcon?: never
}

type ButtonGroupStandardProps = ActionProps | StandardButtonProps

type StyleType =
  | 'primary'
  | 'primary-black'
  | 'primary-blue'
  | 'primary-green'
  | 'primary-red'
  | 'secondary'

export type ButtonGroupProps = {
  /** This is an array of 2 or 3 buttons. You can choose to have a mix of Icon buttons, an Action button, and a standard Button. */
  buttons: Array<StandardButtonProps | ActionProps>
  /** Optionally disable the entire ButtonGroup */
  disabled?: boolean
  /** The styleType prop is a subset of the original Button style types. */
  styleType?: StyleType
  /** Use this prop to apply destructive (red) styling to the entire ButtonGroup */
  destructive?: boolean
  /** Optional prop to add a test id to the ButtonGroup for QA testing */
  qaTestId?: string
}

const ButtonGroup = ({
  buttons,
  disabled,
  styleType = 'secondary',
  destructive = false,
  qaTestId = 'button-group',
}: ButtonGroupProps): React.JSX.Element => {
  if (buttons.length > 3 || buttons.length < 2) {
    throw new Error(c('errorButtonGroupMinButtons'))
  }

  const uniqueIcons = new Set<string>()

  buttons.forEach((buttonProps, index) => {
    // Icon button checks
    if (!buttonProps.actions && buttonProps.icon) {
      // Check for duplicate icons
      if (uniqueIcons.has(buttonProps.icon)) {
        throw new Error(c('errorButtonGroupDuplicateIcons'))
      }
      uniqueIcons.add(buttonProps.icon)

      // Check for destructive buttons with no confirmation
      if (buttonProps.destructive && !buttonProps.confirmation) {
        throw new Error(c('errorButtonGroupDestructiveNoConfirmation'))
      }
    }

    // Action button checks
    // Check for actions position
    if (buttonProps.actions && index > 0) {
      throw new Error(c('errorButtonGroupActionFirstButton'))
    }

    // Standard button checks
    // Check for standard button position
    if (buttonProps.children && index < buttons.length - 1) {
      throw new Error(c('errorButtonGroupStandardLastButton'))
    }
  })

  const getButtonClassName = (index: number): string => {
    const classNames = [styles.left, styles.middle, styles.right]
    if (buttons.length === 2) {
      return index === 1 ? classNames[2] : classNames[index]
    } else {
      return classNames[index]
    }
  }

  return (
    <div className={styles.container} qa-test-id={qaTestId}>
      {buttons.map((buttonProps, index) => {
        return (
          <ButtonGroupContent
            key={index}
            buttonProps={{
              ...buttonProps,
              ...(disabled ? { disabled: true } : {}),
              ...(destructive ? { destructive: true } : {}),
            }}
            className={getButtonClassName(index)}
            styleType={styleType}
          />
        )
      })}
    </div>
  )
}

type ButtonGroupContentProps = {
  buttonProps: ButtonGroupStandardProps
  className: string
  styleType?: StyleType
}

const ButtonGroupContent = ({
  buttonProps,
  className,
  styleType = 'secondary',
}: ButtonGroupContentProps): React.JSX.Element => {
  const [reRenderView, setReRenderView] = useState(false)
  const isMobile = useIsMobileView()
  const popoverPlacement = isMobile ? 'top' : 'left'
  const isIconButton = !buttonProps.actions && buttonProps
  const actionsButton = buttonProps.actions
  const [isTooltipOpen, setIsTooltipOpen] = useState(true)

  // Update class composition to use the appropriate style source
  const buttonClasses = `${className} ${styles.iconButton}`

  if (isIconButton) {
    return (
      // @ts-expect-error Types are getting lost when spreading buttonProps here. Ignoring the type error.
      <Button
        {...buttonProps}
        icon={buttonProps.icon}
        className={buttonClasses}
        styleType={styleType}
        popoverProps={{
          ...buttonProps.popoverProps,
          position: 'top' as PopoverProps['position'],
        }}
      />
    )
  }

  if (actionsButton) {
    return (
      <DropdownMenu
        actions={buttonProps.actions}
        trigger={
          <Button
            disabled={buttonProps.disabled}
            className={`${buttonClasses} ${styles.left} ${styles.actionButton}`}
            styleType={styleType}
            onClick={(e) => {
              e.stopPropagation()
              setIsTooltipOpen(false)
            }}
            onMouseOver={() => setIsTooltipOpen(true)}
            {...(isTooltipOpen ? { tooltip: buttonProps.tooltip } : {})}
            qaTestId={buttonProps.qaTestId}
          >
            <Icon
              icon={buttonProps.actionIcon ? buttonProps.actionIcon : 'options'}
              color='dark-purple'
            />
          </Button>
        }
        popoverProps={{
          position: buttonProps.placement
            ? buttonProps.placement
            : popoverPlacement,
        }}
        reRenderView={() => setReRenderView(!reRenderView)}
      />
    )
  }

  return (
    <Button {...(buttonProps as StandardButtonProps)} styleType={styleType} />
  )
}

export default ButtonGroup
